import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './NondBlog.css';
import { StatRow, HBarChart } from './BlogVisuals';

const OTHER_POSTS = [
  { to: '/nond/blog/how-to-plan-meals-for-the-week', title: 'How to plan meals for the week: a complete step-by-step guide' },
  { to: '/nond/blog/meal-prep-beginners', title: 'Meal prep for beginners: the only system that actually sticks' },
  { to: '/nond/blog/grocery-list-from-meal-plan', title: 'How to build a grocery list from your meal plan automatically' },
];

const NondBlogStopWastingFood: React.FC = () => {
  useEffect(() => {
    document.title = 'How to Stop Wasting Food: A Simple Weekly Meal Planning Routine | Nond';
    let meta = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (!meta) { meta = document.createElement('meta'); meta.name = 'description'; document.head.appendChild(meta); }
    meta.content = 'Learn a practical weekly meal planning routine that stops food waste, saves money, and makes cooking less stressful — no complicated systems required.';
  }, []);

  return (
    <article className="blog-root">
      <Link to="/nond" className="blog-back">← Back to Nond</Link>

      <h1>How to Stop Wasting Food: A Simple Weekly Meal Planning Routine</h1>
      <p className="blog-meta">Meal planning · Food waste · Weekly routine</p>

      <p>
        The average household throws away roughly 30% of the food it buys. That's not a small number — it adds up to hundreds of dollars a year, and a lot of guilt every time you pull a forgotten bag of salad out of the back of the fridge.
      </p>
      <p>
        The root cause is almost always the same: buying without a plan. This guide gives you a dead-simple weekly meal planning routine that fixes that.
      </p>

      <StatRow stats={[
        { value: '30%', label: 'of groceries thrown away', sub: 'average household' },
        { value: '$1,200', label: 'wasted per year', sub: 'US average estimate' },
        { value: '5 min', label: 'to plan the whole week', sub: 'with the right routine' },
      ]} />

      <h2>Why meal planning actually works (when done right)</h2>
      <p>
        Most meal planning advice fails because it asks too much. Elaborate spreadsheets, hour-long Sunday prep sessions, perfectly balanced macros — none of that is sustainable. What works is a lightweight routine that takes five minutes and runs on autopilot once it's a habit.
      </p>
      <p>
        The goal is not to plan every meal perfectly. The goal is to buy only what you will actually use.
      </p>

      <HBarChart
        title="Shopping accuracy: does the method matter?"
        bars={[
          { label: 'From memory', pct: 62, color: 'rgba(255,255,255,0.22)' },
          { label: 'A rough list', pct: 76, color: 'rgba(168,85,247,0.4)' },
          { label: 'From a meal plan', pct: 95, color: '#a855f7' },
        ]}
      />

      <h2>The 5-minute weekly planning routine</h2>
      <ol>
        <li>
          <strong>Check your pantry first.</strong> Before you even think about recipes, do a 60-second scan of your fridge and cupboards. What needs to be used up this week? Plan at least one meal around those ingredients.
        </li>
        <li>
          <strong>Pick 4–5 dinners, not 7.</strong> Leave two nights as leftovers nights or flexible nights. Planning every single meal creates pressure and guarantees waste when life doesn't cooperate.
        </li>
        <li>
          <strong>Write down your actual grocery list from those recipes.</strong> Not from memory — from the recipes. This is where an app like Nond saves you real time: it reads your planned meals and generates the list for you automatically.
        </li>
        <li>
          <strong>Mark your staples.</strong> Olive oil, salt, garlic, butter — mark these once as pantry staples and your shopping list will never include them again. You stop buying duplicates of things you already have.
        </li>
        <li>
          <strong>Shop the list, nothing else.</strong> The list is the plan. Impulse buys are where waste hides.
        </li>
      </ol>

      <div className="blog-callout">
        The single biggest lever: shop from a list generated by your actual meal plan, not from habit or memory. Everything else is secondary.
      </div>

      <h2>How to handle the inevitables</h2>
      <p>
        Plans change. You'll have a late meeting, order takeout on Thursday, or get sick on Friday. Here's how to handle it without wasting food:
      </p>
      <ul>
        <li><strong>Build in one "mystery fridge" meal per week</strong> — a night where you cook whatever needs to be used up. This is your safety valve.</li>
        <li><strong>Batch cook anything that will go bad soon.</strong> If your spinach is about to turn, wilt it into pasta. If bananas are overripe, freeze them.</li>
        <li><strong>Keep a short list of 5-ingredient fallback recipes</strong> you can make from pantry staples. These are your "plan B" for cancelled dinner plans.</li>
      </ul>

      <h2>What a realistic meal planning week actually looks like</h2>
      <p>
        Sunday evening, 5 minutes: open Nond, look at what you have, pick four dinners, generate your list, go to the store on Monday. That's it.
      </p>
      <p>
        Monday: recipe A. Tuesday: recipe B. Wednesday: leftovers from Monday. Thursday: recipe C. Friday: flexible. Saturday: recipe D or takeout. Sunday: whatever's left in the fridge.
      </p>
      <p>
        That's a realistic week. Not perfect. Sustainable.
      </p>

      <h2>The compound effect over time</h2>
      <p>
        After four weeks of this routine, you'll notice three things: your fridge is cleaner, your grocery bill is lower, and you spend less mental energy deciding what to eat. The planning overhead at the start pays back tenfold in stress saved during the week.
      </p>

      <div className="blog-cta">
        <p>Nond makes this routine automatic — plan meals, generate your grocery list, and track what's in your pantry. Free on iPhone, no account required.</p>
        <a
          className="blog-cta-btn"
          href="https://apps.apple.com/us/app/nond-recipe-grocery-planner/id6757132886"
          target="_blank"
          rel="noopener noreferrer"
        >
          Get Nond Free on the App Store
        </a>
      </div>

      <nav className="blog-nav" aria-label="More meal planning guides">
        <p>More guides:</p>
        <ul>
          {OTHER_POSTS.map(({ to, title }) => (
            <li key={to}><Link to={to}>{title}</Link></li>
          ))}
        </ul>
      </nav>
    </article>
  );
};

export default NondBlogStopWastingFood;
